/**
 * useMessageThread — chat thread con realtime
 * Il Diario di Bordo - Frontend
 *
 * - Carica messaggi paginati di un thread
 * - Join automatico alla room Socket.IO `thread:<id>`
 * - Ascolta message:new / :edited / :deleted / :reaction
 * - sendMessage, editMessage, deleteMessage, markRead, addReaction
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import api from '../services/api'
import { useSocket, useSocketEvent } from './useSocket'

const PAGE_SIZE = 50

export function useMessageThread(threadId) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState(null)
  const oldestIdRef = useRef(null)
  const { socket, connected } = useSocket()

  const loadInitial = useCallback(async () => {
    if (!threadId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get(`/chat/threads/${threadId}/messages`, {
        params: { limit: PAGE_SIZE }
      })
      // Backend ritorna ordine DESC: invertiamo per UI (cronologica ASC)
      const list = (data?.data || []).slice().reverse()
      setMessages(list)
      oldestIdRef.current = list[0]?._id || null
      setHasMore(list.length === PAGE_SIZE)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [threadId])

  const loadMore = useCallback(async () => {
    if (!threadId || !oldestIdRef.current || loading) return
    setLoading(true)
    try {
      const { data } = await api.get(`/chat/threads/${threadId}/messages`, {
        params: { limit: PAGE_SIZE, before: oldestIdRef.current }
      })
      const list = (data?.data || []).slice().reverse()
      if (list.length > 0) {
        setMessages((prev) => [...list, ...prev])
        oldestIdRef.current = list[0]._id
      }
      setHasMore(list.length === PAGE_SIZE)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [threadId, loading])

  const sendMessage = useCallback(async (body, mentions = []) => {
    if (!threadId) return null
    try {
      const { data } = await api.post(`/chat/threads/${threadId}/messages`, { body, mentions })
      // Il messaggio verrà aggiunto via Socket.IO `message:new`. Ma se non siamo
      // connessi via WS, lo aggiungiamo subito per UX.
      if (!connected && data?.data) {
        setMessages((prev) => [...prev, data.data])
      }
      return data?.data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [threadId, connected])

  const editMessage = useCallback(async (messageId, body) => {
    const { data } = await api.put(`/chat/messages/${messageId}`, { body })
    return data?.data
  }, [])

  const deleteMessage = useCallback(async (messageId) => {
    await api.delete(`/chat/messages/${messageId}`)
  }, [])

  const markRead = useCallback(async (lastReadMessageId) => {
    if (!threadId || !lastReadMessageId) return
    try {
      await api.put(`/chat/threads/${threadId}/read`, { lastReadMessageId })
    } catch {
      // silent
    }
  }, [threadId])

  const addReaction = useCallback(async (messageId, emoji) => {
    await api.post(`/chat/messages/${messageId}/reactions`, { emoji })
  }, [])

  const removeReaction = useCallback(async (messageId, emoji) => {
    await api.delete(`/chat/messages/${messageId}/reactions`, { data: { emoji } })
  }, [])

  // Join thread room quando WS è connesso
  useEffect(() => {
    if (!threadId || !connected || !socket) return
    socket.emit('thread:join', { threadId })
    return () => {
      socket.emit('thread:leave', { threadId })
    }
  }, [threadId, connected, socket])

  // Eventi realtime sul thread
  useSocketEvent('message:new', (msg) => {
    if (msg?.threadId !== threadId) return
    setMessages((prev) => {
      if (prev.some((m) => m._id === msg._id)) return prev
      return [...prev, msg]
    })
    // Conferma delivery al server: il sender vedrà 2 spunte grigie sul
    // suo messaggio nel proprio ThreadView (room emit).
    if (socket && connected) {
      socket.emit('chat:message:delivered', { messageId: msg._id })
    }
  })

  // Delivery confermata da un altro destinatario: aggiorna deliveredTo + flag.
  // Short-circuit: se l'userId è già nel deliveredTo del messaggio, non
  // creiamo un nuovo array (evita re-render inutile e cascate sui consumer).
  useSocketEvent('chat:message:delivery-update', ({ threadId: tId, messageId, userId, deliveredAt }) => {
    if (tId !== threadId) return
    setMessages((prev) => {
      let changed = false
      const next = prev.map((m) => {
        if (String(m._id) !== String(messageId)) return m
        const deliveredTo = Array.isArray(m.deliveredTo) ? m.deliveredTo : []
        if (deliveredTo.some((d) => String(d.userId) === String(userId))) return m
        changed = true
        const nextDelivered = [...deliveredTo, { userId, deliveredAt }]
        const deliveredToOthers = nextDelivered.some(
          (d) => String(d.userId) !== String(m.senderId?._id || m.senderId)
        )
        return { ...m, deliveredTo: nextDelivered, deliveredToOthers }
      })
      return changed ? next : prev
    })
  })

  // Un altro partecipante ha aperto la chat e fatto markRead: aggiorna
  // readByOthers per i miei messaggi precedenti al lastReadMessageId.
  // Short-circuit: se nessun messaggio cambia (l'altro era già aggiornato a
  // un read più recente), restituiamo prev → niente re-render → niente
  // cascata di markRead lato consumer → niente loop di traffico.
  useSocketEvent('chat:thread-read', ({ threadId: tId, userId, lastReadAt }) => {
    if (tId !== threadId) return
    const readTs = new Date(lastReadAt).getTime()
    setMessages((prev) => {
      let changed = false
      const next = prev.map((m) => {
        if (m.readByOthers) return m
        const sid = String(m.senderId?._id || m.senderId)
        if (sid === String(userId)) return m // ignora messaggi del lettore stesso
        if (new Date(m.createdAt).getTime() <= readTs) {
          changed = true
          return { ...m, readByOthers: true }
        }
        return m
      })
      return changed ? next : prev
    })
  })
  useSocketEvent('message:edited', (msg) => {
    if (msg?.threadId !== threadId) return
    setMessages((prev) => prev.map((m) => m._id === msg._id ? msg : m))
  })
  useSocketEvent('message:deleted', ({ _id, threadId: tId }) => {
    if (tId !== threadId) return
    setMessages((prev) => prev.map((m) => m._id === _id ? { ...m, deleted: true, body: '' } : m))
  })

  // Reazioni live: aggiorna conteggio reazioni sul messaggio specifico
  useSocketEvent('message:reaction', ({ messageId, emoji, userId, action }) => {
    setMessages((prev) => prev.map((m) => {
      if (String(m._id) !== String(messageId)) return m
      const reactions = Array.isArray(m.reactions) ? [...m.reactions] : []
      const idx = reactions.findIndex((r) => r.emoji === emoji)
      if (action === 'add') {
        if (idx === -1) reactions.push({ emoji, count: 1, userIds: [userId] })
        else if (!reactions[idx].userIds.includes(userId)) {
          reactions[idx] = {
            ...reactions[idx],
            count: reactions[idx].count + 1,
            userIds: [...reactions[idx].userIds, userId]
          }
        }
      } else if (action === 'remove' && idx !== -1) {
        const newUserIds = reactions[idx].userIds.filter((u) => String(u) !== String(userId))
        if (newUserIds.length === 0) reactions.splice(idx, 1)
        else reactions[idx] = { ...reactions[idx], count: newUserIds.length, userIds: newUserIds }
      }
      return { ...m, reactions }
    }))
  })

  useEffect(() => {
    loadInitial()
  }, [loadInitial])

  // Re-sync su riconnessione WS o ritorno foreground (Android/PWA in
  // background sospende il WebSocket → potremmo aver perso message:new).
  useEffect(() => {
    if (!threadId || !socket) return
    const onConnect = () => { loadInitial() }
    socket.on('connect', onConnect)
    return () => { socket.off('connect', onConnect) }
  }, [socket, threadId, loadInitial])

  useEffect(() => {
    if (!threadId) return
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadInitial()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => { document.removeEventListener('visibilitychange', onVisibility) }
  }, [threadId, loadInitial])

  return {
    messages,
    loading,
    hasMore,
    error,
    sendMessage,
    editMessage,
    deleteMessage,
    markRead,
    addReaction,
    removeReaction,
    loadMore,
    refresh: loadInitial
  }
}
